import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  subscribe,
  createOrder,
  checkPromoCode,
} from "../../../../api/recruiterSubscriptionsApi";

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const plan = location.state?.plan;

  // Coupon state
  const [coupon, setCoupon] = useState("");
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [couponValue, setCouponValue] = useState(0);
  const [couponMsg, setCouponMsg] = useState("");
  const [loading, setLoading] = useState(false);

  if (!plan) {
    navigate("/dashboard/pricing-plans1");
    return null;
  }

  const basePrice = parseInt(plan.price.replace(",", ""));
  const discountedPrice = Math.max(basePrice - couponValue, 0);
  const taxAmount = discountedPrice * 0.18;
  const totalAmount = discountedPrice + taxAmount;

  // Coupon apply handler
  const handleApplyCoupon = async (e) => {
    e.preventDefault();
    setLoading(true);
    setCouponMsg("");
    try {
      const resp = await checkPromoCode(coupon, basePrice);
      if (resp?.status === 200) {
        setIsCouponApplied(true);
        setCouponValue(
          resp?.body?.data?.data?.credits
            ? resp?.body?.data?.data?.credits
            : resp?.body?.data?.data?.discount,
        );
        setCouponMsg("Coupon applied!");
      } else {
        setCouponMsg(resp?.body?.message || "Invalid coupon");
      }
    } catch (err) {
      setCouponMsg("Error applying coupon");
    }
    setLoading(false);
  };

  // Remove coupon
  const handleRemoveCoupon = () => {
    setIsCouponApplied(false);
    setCouponValue(0);
    setCoupon("");
    setCouponMsg("");
  };

  // Payment handler
  const handlePayment = async () => {
    const response = await subscribe(plan.id);

    if (response.status === 200) {
      const subscriptionId = response.body.savedSubscription._id;
      const couponCode = isCouponApplied ? coupon : undefined;
      const payment = await createOrder(subscriptionId, couponCode);

      if (payment.status === 200) {
        localStorage.setItem("subscription", JSON.stringify(true));
        const href =
          payment.body.data.checkoutPageUrl ?? payment.body.data.paymentLink;
        window.open(href, "_self");
      } else {
        alert("Payment failed");
      }
    } else {
      alert("Subscription creation failed");
    }
  };

  // Only show up to 5 features, and add '...and more' if there are more
  const featuresToShow = plan.features.slice(0, 5);
  const hasMoreFeatures = plan.features.length > 5;

  return (
    <div className="max-w-lg mx-auto my-12 p-8 bg-white rounded-xl shadow">
      <div className="flex justify-between items-start mb-6">
        <h3 className="text-2xl font-bold">Checkout</h3>
        <button
          onClick={() => navigate("/dashboard/pricing-plans1")}
          className="text-gray-500 hover:text-gray-700"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div className="border-b pb-4 mb-6">
        <h4 className="font-semibold text-lg mb-4">{plan.title}</h4>
        <div className="bg-gray-50 rounded-lg p-4 mb-2">
          <ul className="space-y-2">
            {featuresToShow.map((feature, index) => (
              <li key={index} className="flex items-center text-gray-700">
                <svg
                  className="w-5 h-5 text-green-500 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="3"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="text-base">{feature}</span>
              </li>
            ))}
            {hasMoreFeatures && (
              <li className="flex items-center text-gray-400 italic text-sm">
                <svg
                  className="w-4 h-4 text-gray-300 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                ...and more
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* Coupon section */}
      <div className="mb-6">
        <form
          onSubmit={handleApplyCoupon}
          className="flex gap-2 items-center bg-gray-50 rounded-lg p-2"
        >
          <input
            type="text"
            placeholder="Enter coupon code"
            value={coupon}
            onChange={(e) => setCoupon(e.target.value)}
            disabled={isCouponApplied}
            className="border border-gray-300 rounded px-3 py-2 flex-1 bg-white"
          />
          {!isCouponApplied ? (
            <button
              type="submit"
              className="bg-primary text-white px-4 py-2 rounded font-semibold"
              disabled={loading || !coupon}
            >
              {loading ? "Applying..." : "Apply"}
            </button>
          ) : (
            <button
              type="button"
              className="text-red-500 underline"
              onClick={handleRemoveCoupon}
            >
              Remove
            </button>
          )}
        </form>
        {couponMsg && (
          <div
            className={`mt-2 flex items-center text-sm ${isCouponApplied ? "text-green-600" : "text-red-600"}`}
          >
            {isCouponApplied ? (
              <svg
                className="w-4 h-4 mr-1 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <svg
                className="w-4 h-4 mr-1 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            )}
            {couponMsg}
          </div>
        )}
      </div>

      {/* Price summary */}
      <div className="space-y-3 mb-6 bg-gray-50 rounded-lg p-4">
        <div className="flex justify-between">
          <span className="text-gray-600">Base Price</span>
          <span>₹{basePrice.toLocaleString()}</span>
        </div>
        {isCouponApplied && (
          <div className="flex justify-between">
            <span className="text-gray-600">Coupon Discount</span>
            <span className="text-green-600">
              -₹{couponValue.toLocaleString()}
            </span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-gray-600">GST (18%)</span>
          <span>₹{taxAmount.toLocaleString()}</span>
        </div>
        <div className="flex justify-between font-bold text-xl pt-3 border-t border-gray-200">
          <span>Total Amount</span>
          <span className="text-primary">₹{totalAmount.toLocaleString()}</span>
        </div>
      </div>

      <button
        className="w-full bg-primary text-white py-3 rounded-lg hover:bg-primary/90 transition-colors duration-300 font-semibold"
        onClick={handlePayment}
      >
        Proceed to Payment
      </button>
    </div>
  );
};

export default CheckoutPage;
